import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { Mountain } from 'lucide-react'
import { Card, CardContent } from '../ui/card'
import { Carousel, CarouselContent, CarouselItem } from '../ui/carousel'
import { DashboardSection } from './DashboardSection'
import type { DashboardSend } from '../../types/dashboard'

const FREE_SCROLL = { align: 'start', dragFree: true, containScroll: 'trimSnaps' } as const

/** Same shape the training diary builds: /gyms/:slug/boulders/:slug. */
function climbPath(send: DashboardSend): string | null {
  const { location_type, location_slug, climb_type, slug } = send.climb
  if (!location_type || !location_slug || !climb_type) return null
  return `/${location_type}s/${location_slug}/${climb_type}s/${slug}`
}

function SendCard({ send }: { send: DashboardSend }) {
  const { t } = useTranslation()
  const { climb } = send

  return (
    <Card className="h-full transition-colors hover:border-primary">
      <CardContent className="flex h-full flex-col gap-2 p-2">
        {climb.image_small_url ? (
          <img
            src={climb.image_small_url}
            alt=""
            className="aspect-square w-full rounded-md bg-muted object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex aspect-square w-full items-center justify-center rounded-md bg-muted">
            <Mountain className="size-6 text-muted-foreground" />
          </div>
        )}
        <div className="flex items-baseline justify-between gap-1">
          <span className="truncate text-xs font-medium">{climb.name}</span>
          {climb.difficulty && (
            <span className="shrink-0 text-xs font-semibold text-primary">{climb.difficulty}</span>
          )}
        </div>
        <div className="mt-auto flex items-baseline justify-between gap-1 text-[10px] text-muted-foreground">
          <span className="truncate">
            {send.send_type ? t(`trainingDiary.ascentSendType.${send.send_type}`) : ''}
          </span>
          <span className="shrink-0">{format(parseISO(send.topped_at), 'dd/MM')}</span>
        </div>
      </CardContent>
    </Card>
  )
}

interface RecentSendsCarouselProps {
  sends: DashboardSend[]
}

export function RecentSendsCarousel({ sends }: RecentSendsCarouselProps) {
  const { t } = useTranslation()

  return (
    <DashboardSection title={t('dashboard.recentSends.title')}>
      {sends.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('dashboard.recentSends.empty')}</p>
      ) : (
        <Carousel opts={FREE_SCROLL} className="w-full">
          <CarouselContent className="-ml-2">
            {sends.map((send) => {
              const path = climbPath(send)

              return (
                <CarouselItem key={send.id} className="basis-1/3 pl-2">
                  {path ? (
                    <Link to={path} className="block h-full">
                      <SendCard send={send} />
                    </Link>
                  ) : (
                    <SendCard send={send} />
                  )}
                </CarouselItem>
              )
            })}
          </CarouselContent>
        </Carousel>
      )}
    </DashboardSection>
  )
}
